import { useEffect, useState } from 'react';
import { DEBOUNCE_MS, errorMessage } from './journey-explorer-utils';

export type RequestLoader<T> = ((signal: AbortSignal) => Promise<T>) | null;

export interface DebouncedRequest<T> {
	data: T | null;
	loading: boolean;
	error: string | null;
}

export const useDebouncedRequest = <T>(load: RequestLoader<T>, fallbackError: string, attempt: number): DebouncedRequest<T> => {
	const [data, setData] = useState<T | null>(null);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!load) {
			setLoading(false);
			return;
		}
		const controller = new AbortController();
		setLoading(true);
		const timer = window.setTimeout(async () => {
			try {
				const response = await load(controller.signal);
				setData(response);
				setError(null);
			} catch (caught) {
				if (!controller.signal.aborted) {
					setError(errorMessage(caught, fallbackError));
				}
			} finally {
				if (!controller.signal.aborted) {
					setLoading(false);
				}
			}
		}, DEBOUNCE_MS);
		return () => {
			window.clearTimeout(timer);
			controller.abort();
		};
	}, [load, fallbackError, attempt]);

	return { data, loading, error };
};
